import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

/** How long after a clear the new text is set (the same 100ms CDK's `LiveAnnouncer` waits). */
export const STATUS_LIVE_REGION_SET_DELAY_MS = 100;

/** The class the region carries, so a spec or a stale SSR copy can find it. */
export const STATUS_LIVE_REGION_CLASS = 'g15-status-live-region';

/**
 * The polite live region every status message of this app is read from (WCAG 4.1.3), and nobody
 * else's.
 *
 * It replaces CDK's root `LiveAnnouncer`, which is ONE element shared with every Material component
 * that announces (a snackbar's `announcementMessage`, `MatSort`, `MatSelect`): each `announce()`
 * there cancels whatever another caller still has pending, and finding out whether OUR text was the
 * one set meant reading CDK's private `_previousTimeout` and `_liveElement` (MR !197 review). A
 * region of our own is only ever cancelled by us, so the service knows from its own state what was
 * said; Material's announcements keep CDK's element and a screen reader queues the two.
 *
 * Same mechanics as CDK: created on first use and appended to `<body>`, cleared then set
 * {@link STATUS_LIVE_REGION_SET_DELAY_MS} later (so a repeated text is read again), cleared again
 * after the caller's duration, and exposed to the topmost open `aria-modal` overlay through
 * `aria-owns` -- outside one, some browsers drop it from the accessibility tree while the dialog is
 * up.
 */
@Injectable({ providedIn: 'root' })
export class StatusLiveRegion implements OnDestroy {
  private element: HTMLElement | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  /** Resolves the text still waiting on the set delay: `false` when a later call replaces it. */
  private pending: ((set: boolean) => void) | null = null;

  constructor(
    private zone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /**
   * Clear the region and set `text` after the delay; clear it again `duration` ms later. Resolves
   * `true` once the text is set, `false` if a later call replaced it first.
   */
  announce(text: string, duration: number): Promise<boolean> {
    const el = this.region();
    el.textContent = '';
    clearTimeout(this.timer);
    this.pending?.(false);
    this.exposeToModals(el.id);
    return new Promise<boolean>((resolve) => {
      this.pending = resolve;
      this.zone.runOutsideAngular(() => {
        this.timer = setTimeout(() => {
          el.textContent = text;
          this.pending = null;
          this.timer = setTimeout(() => (el.textContent = ''), duration);
          resolve(true);
        }, STATUS_LIVE_REGION_SET_DELAY_MS);
      });
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
    this.pending?.(false);
    this.pending = null;
    this.element?.remove();
    this.element = null;
  }

  private region(): HTMLElement {
    if (this.element) {
      return this.element;
    }
    const stale = this.document.getElementsByClassName(STATUS_LIVE_REGION_CLASS);
    for (let i = stale.length - 1; i >= 0; i--) {
      stale[i].remove();
    }
    const el = this.document.createElement('div');
    el.classList.add(STATUS_LIVE_REGION_CLASS, 'cdk-visually-hidden');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.id = 'g15-status-live-region';
    this.document.body.appendChild(el);
    this.element = el;
    return el;
  }

  /**
   * Only the TOPMOST modal owns the region. An element can be owned once, and browsers resolve a
   * duplicate `aria-owns` to the first owner in DOM order -- with two dialogs stacked that is the
   * lower one, which the top dialog's `aria-modal` takes out of the tree, so every announcement made
   * while they were stacked was unreadable (MR !197 review, round 19). The overlay container appends
   * in opening order, so the last match is the one in front; the id is taken off every other.
   */
  private exposeToModals(id: string): void {
    const modals = Array.from(this.document.querySelectorAll('body > .cdk-overlay-container [aria-modal="true"]'));
    const top = modals[modals.length - 1];
    modals.forEach((modal) => {
      const tokens = (modal.getAttribute('aria-owns') ?? '')
        .split(' ')
        .filter((t) => t !== '' && t !== id);
      if (modal === top) {
        tokens.push(id);
      }
      if (tokens.length > 0) {
        modal.setAttribute('aria-owns', tokens.join(' '));
      } else {
        modal.removeAttribute('aria-owns');
      }
    });
  }
}
